import { useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import { FaSearch } from "react-icons/fa";
import { RxCaretLeft, RxCaretRight } from "react-icons/rx";

export default function Calendar({
  setOpenSidebar,
  setClickedEvent,
  getAllPost,
}) {
  const calendarRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });

  const [activeFilter, setActiveFilter] = useState("Week");
  const [activeToday, setActiveToday] = useState("Today");
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (getAllPost && getAllPost.length > 0) {
      const calendarEvents = getAllPost.map((post) => {
        let eventDate;
        if (post.schedule?.isScheduled && post.schedule?.scheduledTime) {
          eventDate = new Date(post.schedule.scheduledTime);
        } else {
          eventDate = new Date(post.publishedAt);
        }
        return {
          id: post._id,
          title: post.caption,
          start: eventDate,
          end: eventDate,
          extendedProps: {
            images: post.images,
            platforms: post.platforms,
            accounts: post.accounts,
            status: post.status,
            isScheduled: post.schedule?.isScheduled || false,
          },
          backgroundColor: post.schedule?.isScheduled ? "#4CAF50" : "#2196F3",
          borderColor: post.schedule?.isScheduled ? "#4CAF50" : "#2196F3",
        };
      });

      setEvents(calendarEvents);
    }
  }, [getAllPost]);

  const updateCurrentDate = () => {
    if (calendarRef.current) {
      setCurrentDate(calendarRef.current.getApi().getDate());
    }
  };

  const handleToday = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
      updateCurrentDate();
      setActiveToday("Today");
    }
  };

  const handlePrev = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().prev();
      updateCurrentDate();
      setActiveToday("prev");
    }
  };
  const handleNext = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().next();
      updateCurrentDate();
      setActiveToday("next");
    }
  };

  const changeView = (view) => {
    setCurrentView(view);
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(view);
      updateCurrentDate();
      handleToday();
    }
  };

  return (
    <div className="p-4 bg-white relative pb-10 rounded-lg w-full ">
      <div className="flex justify-between items-center pb-4 border-b">
        <div className="flex gap-1 items-center">
          <button
            onClick={handlePrev}
            className={`px-2 ${
              activeToday === "prev" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-[7px] hover:bg-blue-500  transition-all hover:-translate-x-1 duration-700 ease-out hover:text-white rounded`}
          >
            <RxCaretLeft className="text-lg" />
          </button>
          <button
            onClick={handleToday}
            className={`px-4 ${
              activeToday === "Today" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-2 hover:bg-blue-500  transition-all hover:-translate-y-1 duration-700 ease-out hover:text-white rounded`}
          >
            Today
          </button>
          <button
            onClick={handleNext}
            className={`px-2 ${
              activeToday === "next" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-[7px] hover:bg-blue-500  transition-all hover:translate-x-1 duration-700 ease-out hover:text-white rounded`}
          >
            <RxCaretRight className="text-lg" />
          </button>
        </div>
        <div className="text-lg font-semibold">
          {currentView.includes("Day") &&
            activeFilter === "Day" &&
            new Intl.DateTimeFormat("en-US", { dateStyle: "full" }).format(
              currentDate
            )}
          {currentView.includes("Week") &&
            activeFilter === "Week" &&
            `Week: ${new Intl.DateTimeFormat("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }).format(currentDate)}`}
          {currentView.includes("Month") &&
            activeFilter === "Month" &&
            new Intl.DateTimeFormat("en-US", {
              month: "long",
              year: "numeric",
            }).format(currentDate)}
          {currentView.includes("Year") &&
            activeFilter === "Year" &&
            `Year: ${new Intl.DateTimeFormat("en-US", {
              year: "numeric",
            }).format(currentDate)}`}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              changeView("timeGridDay");
              setActiveFilter("Day");
            }}
            className={`px-4 ${
              activeFilter === "Day" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-2  hover:bg-blue-500 transition-all hover:-translate-y-1 duration-700 ease-out hover:text-white  rounded`}
          >
            Day
          </button>
          <button
            onClick={() => {
              changeView("timeGridWeek");
              setActiveFilter("Week");
            }}
            className={`px-4 ${
              activeFilter === "Week" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-2  transition-all hover:-translate-y-1 duration-700 ease-out  hover:bg-blue-500 hover:text-white  rounded`}
          >
            Week
          </button>
          <button
            onClick={() => {
              changeView("dayGridMonth");
              setActiveFilter("Month");
            }}
            className={`px-4 ${
              activeFilter === "Month"
                ? "bg-blue-500 text-white"
                : "bg-gray-200"
            } py-2  transition-all hover:-translate-y-1 duration-700 ease-out hover:bg-blue-500 hover:text-white rounded`}
          >
            Month
          </button>
          <button
            onClick={() => {
              changeView("multiMonthYear");
              setActiveFilter("Year");
            }}
            className={`px-4 ${
              activeFilter === "Year" ? "bg-blue-500 text-white" : "bg-gray-200"
            } py-2  transition-all hover:-translate-y-1 duration-700 ease-out hover:bg-blue-500 hover:text-white  rounded`}
          >
            Year
          </button>
        </div>
        <div className="flex items-center bg-gray-100 rounded-md px-3 py-2 shadow">
          <FaSearch className="w-4 h-4 text-black opacity-70" />
          <input
            type="text"
            placeholder="Search"
            className="ml-2 bg-transparent text-blue-600 text-xs outline-none placeholder-blue-600"
          />
        </div>
      </div>
      <FullCalendar
        events={events}
        eventClick={(info) => {
          setClickedEvent(
            getAllPost.find((post) => post._id === info.event.id)
          );
          setOpenSidebar(true);
        }}
        ref={calendarRef}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          multiMonthPlugin,
          interactionPlugin,
        ]}
        initialView={currentView}
        headerToolbar={false}
        editable={true}
        selectable={true}
        height="60vh"
        dayHeaderContent={(args) => (
          <div className="flex flex-col">
            <span className="text-gray-700 font-bold text-[8px] uppercase">
              {args.view.calendar.formatDate(args.date, { weekday: "short" })}
            </span>
            <span className="text-blue-500 font-bold text-xl">
              {args.date.getDate()}
            </span>
          </div>
        )}
        eventContent={(arg) => {
          let startTime = arg.event.start.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          const handleEventClick = () => {
            const clickedEventData = events.find(
              (event) =>
                event.title === arg.event.title &&
                event.start === arg.event.start.toISOString()
            );

            if (clickedEventData) {
              setOpenSidebar(true);
              setClickedEvent(clickedEventData);
            }
          };
          const showTooltip = (e) => {
            setTooltip({
              visible: true,
              text: arg.event.title,
              x: e.clientX - 15,
              y: e.clientY + 25,
            });
          };

          const hideTooltip = () => {
            setTooltip({ visible: false, text: "", x: 0, y: 0 });
          };

          return (
            <button
              onClick={handleEventClick}
              onMouseEnter={showTooltip}
              onMouseMove={showTooltip}
              onMouseLeave={hideTooltip}
              className="flex items-center  flex-col  pl-2 text-blue-600 font-medium"
            >
              <div className="flex  items-center">
                {" "}
                <span>{startTime}</span>
              </div>
            </button>
          );
        }}
        dayHeaderClassNames="custom-day-header"
        dayCellClassNames="border-none"
      />
      {tooltip.visible && (
        <div
          className="fixed bg-white text-xs px-2 py-1 break-words max-w-[200px] rounded shadow-lg z-50 pointer-events-none"
          style={{ top: tooltip.y, left: tooltip.x }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
